/**
 * Board Certification Context — Phase 2F: API-Driven
 * REMOVED: sessionStorage persistence, initialCertifications mock data
 * Backend APIs used: GET/POST /api/certifications, PUT /api/certifications/:id/verify|reject|revoke
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import { useAuthGate } from '../hooks/useAuthGate';
// TREATX-BATCH-BOOT-01 (residual sweep) — /api/certifications is a
// provider/admin-scope catalog; patients must not fetch it (403 noise).
const _CERT_ROLES = [
  'clinic_admin', 'provider_admin', 'clinic_staff',
  'admin', 'treatx_admin', 'super_admin', 'platform_admin',
];


// Exported constants used by admin pages
export const CERTIFICATION_STATUS = {
  PENDING: 'pending',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
};

const BoardCertificationContext = createContext();

export const useBoardCertification = () => {
  const ctx = useContext(BoardCertificationContext);
  if (!ctx) throw new Error('useBoardCertification must be used within BoardCertificationProvider');
  return ctx;
};

export const BoardCertificationProvider = ({ children }) => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch certifications from backend
  const fetchCertifications = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.providerId) params.set('provider_id', filters.providerId);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/certifications${qs}`);
      const data = res.data;
      if (data.success) setCertifications(data.certifications);
    } catch (e) { console.error('Failed to fetch certifications:', e); }
    setLoading(false);
  }, []);

  // TREATX-BATCH-BOOT-01 — `/api/certifications` is auth-protected
  // (empirically returns 401 anonymously past the soft-launch gate).
  // Gate the initial fetch on canonical auth-ready state.
  const { authorized: _cert_authorized } = useAuthGate({ roles: _CERT_ROLES });
  useEffect(() => {
    if (!_cert_authorized) return;
    fetchCertifications();
  }, [_cert_authorized, fetchCertifications]);

  // Get certifications by provider
  const getCertificationsByProvider = useCallback((providerId) => {
    return certifications.filter(c => c.providerId === providerId);
  }, [certifications]);

  // Get certifications by status
  const getCertificationsByStatus = useCallback((status) => {
    return certifications.filter(c => c.status === status);
  }, [certifications]);

  // Submit new certification
  const submitCertification = useCallback(async (data) => {
    try {
      const res = await api.post(`/certifications`, data);
      const result = res.data;
      if (result.success) {
        await fetchCertifications();
        return result.certification;
      }
    } catch (e) { console.error('Failed to submit certification:', e); }
    return null;
  }, [fetchCertifications]);

  // Verify certification
  const verifyCertification = useCallback(async (certId, verifiedBy = 'TreatX Admin') => {
    try {
      const res = await api.put(`/certifications/${certId}/verify?verified_by=${encodeURIComponent(verifiedBy)}`);
      const result = res.data;
      if (result.success) {
        await fetchCertifications();
        return result.certification;
      }
    } catch (e) { console.error('Failed to verify certification:', e); }
    return null;
  }, [fetchCertifications]);

  // Reject certification
  const rejectCertification = useCallback(async (certId, reason = '', rejectedBy = 'TreatX Admin') => {
    try {
      const params = new URLSearchParams({ reason, rejected_by: rejectedBy });
      const res = await api.put(`/certifications/${certId}/reject?${params}`);
      const result = res.data;
      if (result.success) {
        await fetchCertifications();
        return result.certification;
      }
    } catch (e) { console.error('Failed to reject certification:', e); }
    return null;
  }, [fetchCertifications]);

  // Revoke certification
  const revokeCertification = useCallback(async (certId, reason = '', revokedBy = 'TreatX Admin') => {
    try {
      const params = new URLSearchParams({ reason, revoked_by: revokedBy });
      const res = await api.put(`/certifications/${certId}/revoke?${params}`);
      const result = res.data;
      if (result.success) {
        await fetchCertifications();
        return result.certification;
      }
    } catch (e) { console.error('Failed to revoke certification:', e); }
    return null;
  }, [fetchCertifications]);

  // Check if provider is board certified
  const isProviderBoardCertified = useCallback((providerId) => {
    return certifications.some(c => c.providerId === providerId && c.status === 'verified');
  }, [certifications]);

  // P0-2: Sort clinics by ranking (board-certified first, then by certification count)
  const sortByRanking = useCallback((clinics, options = {}) => {
    if (!clinics || !Array.isArray(clinics)) return clinics || [];
    return [...clinics].sort((a, b) => {
      const aCert = certifications.some(c => c.providerId === (a.id || a.clinic_id) && c.status === 'verified');
      const bCert = certifications.some(c => c.providerId === (b.id || b.clinic_id) && c.status === 'verified');
      if (aCert && !bCert) return -1;
      if (!aCert && bCert) return 1;
      return 0;
    });
  }, [certifications]);

  // P0-2: Filter to only board-certified clinics
  const filterBoardCertifiedOnly = useCallback((clinics) => {
    if (!clinics || !Array.isArray(clinics)) return [];
    return clinics.filter(c => {
      return certifications.some(cert => cert.providerId === (c.id || c.clinic_id) && cert.status === 'verified');
    });
  }, [certifications]);

  return (
    <BoardCertificationContext.Provider value={{
      certifications,
      loading,
      fetchCertifications,
      getCertificationsByProvider,
      getCertificationsByStatus,
      submitCertification,
      verifyCertification,
      rejectCertification,
      revokeCertification,
      isProviderBoardCertified,
      sortByRanking,
      filterBoardCertifiedOnly,
    }}>
      {children}
    </BoardCertificationContext.Provider>
  );
};

export default BoardCertificationContext;
